import DOMPurify from 'dompurify';
/** @typedef {import('./HtmlUtils.svc')} Types */
//
export class HtmlUtils {
	constructor($log) {
		'ngInject';
		this.log = $log;
	}

	/**
	 * @param {string} str
	 * @returns {string} str with a trailing dot ensured
	 */
	_addDotToEndOfString(str) {
		if (!str.endsWith('.')) return str + '.';
		return str;
	}

	/**
	 * @param {string} htmlContent
	 * @param {number} [maxLength=550]
	 * @returns {string}
	 */
	buildExcerpt(htmlContent, maxLength = 550) {
		if (!htmlContent) {
			this.log.warn('param: htmlContent, is required');
			return ' ';
		}

		const hasLimit = maxLength > 0;
		const withoutSpaceEntities = htmlContent.replace(/&ZeroWidthSpace;|&nbsp;/g, ' ');
		const element = (() => {
			const el = document.createElement('div');
			el.innerHTML = withoutSpaceEntities;
			return el;
		})();

		let excerpt = '';
		let charCount = 0;
		const nodes = element.childNodes;

		for (let i = 0; i < nodes.length; i++) {
			if (hasLimit && charCount >= maxLength) break;

			const node = nodes[i];
			if (node.nodeType === Node.TEXT_NODE) {
				const cleaned = node.textContent.replace(/\s+/g, ' ').trim();
				const words = cleaned ? this._addDotToEndOfString(cleaned).split(/\s+/) : [];

				for (let j = 0; j < words.length; j++) {
					const word = words[j];

					if (charCount + word.length + 1 > maxLength) {
						break;
					}

					excerpt += word + ' ';
					charCount += word.length + 1;
				}
			} else if (node.nodeType === Node.ELEMENT_NODE) {
				if (node.nodeName === 'BR') {
					if (charCount + 1 > maxLength) break;

					excerpt += '\n';
					charCount += 1;
				} else if (node.nodeName === 'IMG') {
					// Skip over image elements and do not count them towards word count
					continue;
				} else if (node.nodeName.toUpperCase() === 'P' || node.nodeName.toUpperCase() === 'UL') {
					// short-circuit to avoid recursive calls
					if (charCount >= maxLength) return excerpt.trim();

					const subExcerpt = this.buildExcerpt(node.innerHTML, maxLength - charCount);

					if (subExcerpt.length > 0) {
						const formatted = '\n' + subExcerpt.trim().replace(/\n/g, '\n ') + '\n';
						excerpt += formatted;
						charCount += formatted.length;
					}
				} else if (node.nodeName === 'LI') {
					const subExcerpt = this.buildExcerpt(node.innerHTML, maxLength - charCount);

					if (subExcerpt.length > 0) {
						excerpt += '- ' + subExcerpt.trim() + '\n';
						charCount += subExcerpt.length + 3; // Add 3 for '- ' prefix and the '\n'
					}
				} else {
					const subExcerpt = this.buildExcerpt(node.innerHTML, maxLength - charCount);

					if (subExcerpt.length > 0) {
						excerpt += subExcerpt + ' ';
						charCount += subExcerpt.length + 1;
					}
				}
			}
		}

		return excerpt.trimEnd();
	}

	/**
	 * @param {string} html
	 * @param {string[]} [exclude=[]] tags to drop from the default allow-list
	 * @returns {string}
	 */
	sanitizeHtml(html, exclude = []) {
		if (!html) return '';

		const excludeSet = new Set(exclude);
		const DEFAULT_ALLOWED = [
			'p',
			'br',
			'strong',
			'em',
			'blockquote',
			'code',
			'img',
			'a',
			'h1',
			'h2',
			'h3',
			'h4',
			'h5',
			'h6',
			'ul',
			'ol',
			'li',
			'dl',
			'dt',
			'dd',
		];
		const ALLOWED_TAGS = DEFAULT_ALLOWED.filter(tag => !excludeSet.has(tag));

		// creates a new DOM tree
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');
		const result = document.createElement('div');

		// process a node and its children
		function cleanNode(node) {
			// text nodes, just create a copy
			if (node.nodeType === Node.TEXT_NODE) {
				return document.createTextNode(node.textContent);
			}

			// skip non-element nodes
			if (node.nodeType !== Node.ELEMENT_NODE) {
				return null;
			}

			const tagName = node.tagName.toLowerCase();

			// if it's not an allowed tag, just process its children
			if (!ALLOWED_TAGS.includes(tagName)) {
				const fragment = document.createDocumentFragment();

				for (const child of node.childNodes) {
					const cleanedChild = cleanNode(child);
					if (cleanedChild) {
						fragment.appendChild(cleanedChild);
					}
				}

				return fragment;
			}

			// new clean element
			const cleanElement = document.createElement(tagName);

			// copy the href attribute for links
			if (tagName === 'a' && node.hasAttribute('href')) {
				cleanElement.setAttribute('href', node.getAttribute('href'));
			}

			// all child nodes
			for (const child of node.childNodes) {
				const cleanedChild = cleanNode(child);
				if (cleanedChild) {
					cleanElement.appendChild(cleanedChild);
				}
			}

			return cleanElement;
		}

		// body content
		const bodyContent = doc.body;
		for (const childNode of bodyContent.childNodes) {
			const cleanedNode = cleanNode(childNode);
			if (cleanedNode) {
				result.appendChild(cleanedNode);
			}
		}

		return result.innerHTML;
	}

	/**
	 * @param {string} dirty
	 * @param {Object} [config={}] DOMPurify config overrides
	 * @returns {string}
	 */
	sanitizeRichHTML(dirty, config = {}) {
		if (!dirty) return '';
		const defaultConfig = {
			ALLOWED_TAGS: [
				'b',
				'strong',
				'i',
				'em',
				'u',
				'ul',
				'ol',
				'li',
				'a',
				'p',
				'br',
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6',
				'blockquote',
				'code',
			],
			ALLOWED_ATTR: ['href', 'title', 'target'],
		};
		return DOMPurify.sanitize(dirty, {...defaultConfig, ...config});
	}

	/**
	 * @param {string} htmlContent
	 * @param {string[]} [extraTags=[]] extra tag names to unwrap
	 * @param {Types.CleanHtmlHeadings|false} [changeHeadings=false] heading downgrade rule
	 * @returns {string}
	 */
	cleanHTML(htmlContent, extraTags = [], changeHeadings = false) {
		if (!htmlContent) return '';
		const container = document.createElement('div');
		container.innerHTML = htmlContent;

		const blacklist = new Set(['STYLE', 'BR', 'DIV', 'SECTION', 'ARTICLE', 'IMG']);
		if (extraTags.length > 0) extraTags.forEach(tag => blacklist.add(tag.toUpperCase()));

		const headings =
			changeHeadings && changeHeadings.from && changeHeadings.to
				? new Set(changeHeadings.from.map(h => h.toUpperCase()))
				: null;

		function traverse(node) {
			if (node.nodeType === Node.ELEMENT_NODE) {
				node.removeAttribute('class');
				node.removeAttribute('style');

				// open links in new tab
				if (node.nodeName === 'A') node.setAttribute('target', '_blank');

				// replace heading elements
				if (headings && headings.has(node.nodeName)) {
					const downgraded = document.createElement(changeHeadings.to.toUpperCase());
					while (node.firstChild) {
						downgraded.appendChild(node.firstChild);
					}
					node.parentNode.replaceChild(downgraded, node);
					// eslint-disable-next-line
					node = downgraded;
				}

				const children = [...node.childNodes];
				children.forEach(traverse);

				if (blacklist.has(node.nodeName)) {
					while (node.firstChild) {
						node.parentNode.insertBefore(node.firstChild, node);
					}
					node.remove();
				}
			} else if (node.nodeType === Node.TEXT_NODE) {
				node.textContent = node.textContent.replace(/\s+/g, ' ');
			}
		}

		[...container.childNodes].forEach(traverse);

		// normalize multiple spaces
		container.innerHTML = container.innerHTML.replace(/\s{2,}/g, ' ').trim();

		return container.innerHTML;
	}

	/**
	 * @param {string} html
	 * @returns {string} indented HTML
	 */
	formatHtml(html) {
		if (!html) return '';

		let formatted = '';
		let indent = 0;

		// get indentation string
		function getIndent(level) {
			// level is non-negative to avoid RangeError
			const safeLevel = Math.max(0, level);
			return '  '.repeat(safeLevel);
		}

		// split HTML by tags
		const parts = html.split(/(<\/?[^>]+>)/);

		for (let i = 0; i < parts.length; i++) {
			const part = parts[i].trim();
			if (!part) continue;

			// if it's a tag
			if (part.startsWith('<')) {
				// closing tag
				if (part.startsWith('</')) {
					indent = Math.max(0, indent - 1); // Prevent negative indent
					formatted += getIndent(indent) + part + '\n';
				}
				// self-closing tag
				else if (part.endsWith('/>')) {
					formatted += getIndent(indent) + part + '\n';
				}
				// opening tag
				else {
					formatted += getIndent(indent) + part + '\n';
					// Don't increase indent for inline elements
					if (!part.match(/<(strong|em|code|a)[^>]*>/i)) {
						indent++;
					}
				}
			}
			// content
			else if (part) {
				formatted += getIndent(indent) + part + '\n';
			}
		}

		return formatted;
	}
}
//
let compMod = angular.module('mod.svc.HtmlUtils', []).service('HtmlUtilsSvc', HtmlUtils);
export default (compMod = compMod.name);
